/**
 * DebtManager — add/edit multiple debts + avalanche/snowball simulator.
 */
import { useState } from "react";
import { Trash2, Plus, Play } from "lucide-react";
import { NumberField, TextField, SelectField, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEBT_TYPES = [
  ["credit_card", "Credit card"],
  ["student_loan", "Student loan"],
  ["mortgage", "Mortgage"],
  ["auto", "Auto loan"],
  ["personal", "Personal loan"],
  ["line_of_credit", "Line of credit"],
  ["tax", "Tax debt"],
  ["other", "Other"],
];

export default function DebtManager() {
  const { plan, summary, patch } = useFinancialPlan();
  const debts = plan?.debts || [];
  const [busy, setBusy] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [extraMonthly, setExtraMonthly] = useState(0);

  const upsert = async (updated) => {
    setBusy(true);
    try { await patch({ debts: updated }); } finally { setBusy(false); }
  };
  const addDebt = () => upsert([...debts, {
    id: `d_${Date.now()}`, name: "New debt", type: "credit_card",
    original_balance: 0, current_balance: 0, interest_rate_pct: 0,
    min_monthly_payment: 0, current_monthly_payment: 0, is_variable_rate: false,
    payment_frequency: "monthly",
  }]);
  const removeDebt = (id) => upsert(debts.filter((d) => d.id !== id));
  const patchDebt = (id, changes) => upsert(debts.map((d) => d.id === id ? { ...d, ...changes } : d));

  const runSim = async () => {
    try {
      const { data } = await api.post("/financial-plan/simulate-payoff", { extra_monthly: Number(extraMonthly) || 0, method: "both" });
      setSimulation(data);
    } catch (e) { toast.error("Simulation failed"); }
  };

  const s = summary || {};

  return (
    <div className="space-y-4" data-testid="debt-manager">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <StatCell label="Total debt" value={<Money amount={s.total_debt} currency={plan?.currency} />} sub={`${debts.length} debt${debts.length === 1 ? "" : "s"}`} testId="debt-total" tone={s.total_debt > 0 ? "warn" : "default"} />
        <StatCell label="Weighted APR" value={`${s.weighted_avg_interest_rate_pct}%`} testId="debt-wapr" tone={s.weighted_avg_interest_rate_pct >= 15 ? "bad" : "default"} />
        <StatCell label="Monthly payments" value={<Money amount={s.total_debt_monthly} currency={plan?.currency} />} testId="debt-monthly" />
        <StatCell label="DTI" value={`${s.debt_to_income_pct}%`} sub="of gross income" testId="debt-dti" tone={s.debt_to_income_pct > 43 ? "bad" : s.debt_to_income_pct > 36 ? "warn" : "good"} />
      </div>

      {debts.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
          No debts tracked yet — nice! If you have any, add them to get personalized payoff plans.
        </div>
      )}

      {debts.map((d) => (
        <div key={d.id} className="border border-border rounded-lg p-4 space-y-3" data-testid={`debt-row-${d.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField label="Name" value={d.name} onChange={(v) => patchDebt(d.id, { name: v })} testId={`debt-name-${d.id}`} />
            <SelectField label="Type" value={d.type} onChange={(v) => patchDebt(d.id, { type: v })} options={DEBT_TYPES} testId={`debt-type-${d.id}`} />
            <TextField label="Lender" value={d.lender || ""} onChange={(v) => patchDebt(d.id, { lender: v })} placeholder="Chase, Amex, etc." />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <NumberField label="Current balance" value={d.current_balance} onChange={(v) => patchDebt(d.id, { current_balance: v })} prefix={plan?.currency} testId={`debt-balance-${d.id}`} />
            <NumberField label="Interest rate" value={d.interest_rate_pct} onChange={(v) => patchDebt(d.id, { interest_rate_pct: v })} suffix="%" testId={`debt-rate-${d.id}`} />
            <NumberField label="Min monthly" value={d.min_monthly_payment} onChange={(v) => patchDebt(d.id, { min_monthly_payment: v })} prefix={plan?.currency} testId={`debt-min-${d.id}`} />
            <NumberField label="Current monthly" value={d.current_monthly_payment} onChange={(v) => patchDebt(d.id, { current_monthly_payment: v })} prefix={plan?.currency} testId={`debt-current-${d.id}`} />
          </div>
          <div className="flex justify-end">
            <button onClick={() => removeDebt(d.id)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid={`debt-remove-${d.id}`}>
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      ))}

      <Button size="sm" variant="outline" onClick={addDebt} disabled={busy} data-testid="debt-add-btn">
        <Plus size={13} className="mr-1" /> Add debt
      </Button>

      {debts.length >= 1 && (
        <div className="border border-primary/30 bg-primary/6 rounded-xl p-4 mt-4" data-testid="payoff-simulator">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Payoff simulator</div>
              <div className="text-sm text-muted-foreground mt-1">Add extra monthly payments and compare Avalanche vs Snowball.</div>
            </div>
            <div className="flex items-end gap-2">
              <NumberField label={`Extra / mo (${plan?.currency})`} value={extraMonthly} onChange={setExtraMonthly} testId="payoff-extra" />
              <Button size="sm" onClick={runSim} data-testid="payoff-run-btn"><Play size={12} className="mr-1" /> Simulate</Button>
            </div>
          </div>
          {simulation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <PayoffCard title="Avalanche · highest interest first" result={simulation.avalanche} currency={plan?.currency} testId="payoff-avalanche" />
              <PayoffCard title="Snowball · smallest balance first" result={simulation.snowball} currency={plan?.currency} testId="payoff-snowball" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PayoffCard({ title, result, currency, testId }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={testId}>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="text-2xl font-black mt-1 tabular-nums">{result.years} yrs</div>
      <div className="text-xs text-muted-foreground">Total interest: <Money amount={result.total_interest} currency={currency} /></div>
      {result.order?.length > 0 && (
        <ol className="mt-2 text-xs space-y-1">
          {result.order.map((o) => (
            <li key={o.id} className="flex items-center gap-1"><span className="text-primary font-mono">m{o.paid_off_month}</span> <span>{o.name}</span></li>
          ))}
        </ol>
      )}
    </div>
  );
}
